import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { NotificationContext, type Notification, type NotificationType } from './NotificationContext';

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const addNotification = useCallback((message: string, type: NotificationType) => {
    const id = Math.random().toString(36).substring(2, 9);
    setNotifications((prev) => [...prev, { id, message, type }]);

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 3000);
  }, [removeNotification]);

  const showSuccess = useCallback((message: string) => addNotification(message, 'success'), [addNotification]);
  const showError = useCallback((message: string) => addNotification(message, 'error'), [addNotification]);
  const showWarning = useCallback((message: string) => addNotification(message, 'warning'), [addNotification]);
  const showInfo = useCallback((message: string) => addNotification(message, 'info'), [addNotification]);

  useEffect(() => {
    const handleRateLimit = (e: Event) => {
      const retryAfter = (e as CustomEvent).detail?.retryAfter || '1';
      showWarning(`System is busy. Retrying in ${retryAfter}s...`);
    };
    window.addEventListener('api-rate-limited', handleRateLimit);
    return () => window.removeEventListener('api-rate-limited', handleRateLimit);
  }, [showWarning]);

  const contextValue = useMemo(() => ({
    notifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
  }), [notifications, showSuccess, showError, showWarning, showInfo, removeNotification]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
